import {addDays, startOfToday} from 'date-fns';

import {DeepPartial} from 'react-hook-form';

import {isNotNil} from 'ramda-adjunct';

import {BranchResponseBody} from '@dms/api/shared';
import {
  getBankAccountDetails,
  getDefaultBankAccountFromBranch,
  useGetDocumentData,
} from '@dms/shared';

import {getApiDateString, Nullish} from 'shared/web';

import {AccountingDocumentFormValues} from '../../../types';
import {mapInvoiceItems} from './mapInvoiceItems';

const CORRECTIVE_TAX_DUE_DAYS = 14;

export type GetDefaultCorrectiveTaxDocumentFormValuesArgs = {
  accountingDocument: ReturnType<typeof useGetDocumentData>['data'];
  invoiceItems: ReturnType<typeof mapInvoiceItems>;
  isSellerAsSupplier: boolean;
  currentBranch: BranchResponseBody | Nullish;
  isBranchSynced: boolean;
};

export const getDefaultCorrectiveTaxDocumentFormValues = ({
  accountingDocument,
  invoiceItems,
  isSellerAsSupplier,
  currentBranch,
  isBranchSynced,
}: GetDefaultCorrectiveTaxDocumentFormValuesArgs): DeepPartial<AccountingDocumentFormValues> => {
  const originBankAccount = accountingDocument?.paymentInfo?.bankAccount;
  const defaultBankAccountResult = getDefaultBankAccountFromBranch(
    originBankAccount,
    currentBranch,
    isBranchSynced,
    false
  );

  const bankAccountDetails = getBankAccountDetails(
    defaultBankAccountResult.selectedBankAccount,
    defaultBankAccountResult.availableBankAccounts
  );

  // Derived as one object: a correction either carries the original document's payment details
  // over (mode 2, where the seller's account is not in the active branch) or defaults to the
  // branch, and the account must never end up paired with the other source's iban/swift.
  const defaultPaymentInfo = isSellerAsSupplier
    ? {
        bankAccount: originBankAccount,
        iban: accountingDocument?.paymentInfo?.iban ?? null,
        swift: accountingDocument?.paymentInfo?.swift ?? null,
      }
    : {
        bankAccount: defaultBankAccountResult.selectedBankAccount,
        iban: bankAccountDetails.iban,
        swift: bankAccountDetails.swift,
      };

  const today = startOfToday();
  const todayDate = getApiDateString(today);

  return {
    additionalCustomer: isNotNil(accountingDocument?.additionalCustomer?.customer.id)
      ? {
          ...accountingDocument?.additionalCustomer,
          customer: {
            tradeName: null,
            ...accountingDocument?.additionalCustomer?.customer,
            id: accountingDocument?.additionalCustomer?.customer.id,
          },
        }
      : null,
    currency: accountingDocument?.currency,
    customer: accountingDocument?.customer,
    customFieldsPayload: accountingDocument?.customFields,
    issueDate: todayDate,
    dueDate: getApiDateString(
      addDays(today, accountingDocument?.dueSinceIssueDateInDays ?? CORRECTIVE_TAX_DUE_DAYS)
    ),
    dateOfTaxableSupply: todayDate,
    dateOfReception: accountingDocument?.dateOfReception,
    internalNote: accountingDocument?.internalNote,
    paymentInfo: {
      ...accountingDocument?.paymentInfo,
      ...defaultPaymentInfo,
    },
    id: accountingDocument?.id,
    supplier: accountingDocument?.supplier,
    invoiceItems,
    documentSeriesId: accountingDocument?.documentSeriesId,
    exchangeRateRatio: accountingDocument?.exchangeRateRatio,
    number: accountingDocument?.number,
    reason: null,
    isExchangeRateRatioEnabled: accountingDocument?.isExchangeRateRatioEnabled,
    dueSinceIssueDateInDays: accountingDocument?.dueSinceIssueDateInDays,
    footerNote: accountingDocument?.footerNote,
    isInternal: accountingDocument?.invoiceCategory === 'internal',
  };
};
